import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { ForwardedRef, Fragment, useMemo, useState } from 'react';

import { usePublicAppContext } from '_core/context/public-app-context';
import { useScrollWithRefs } from '_core/hooks/use-scroll-with-refs';
import { PublicApiRepo, useGetLinks, useGetPage } from '_core/repos/public-api.repo';
import { createLevels } from '_core/utils/public-links/createLevels';
import { services } from '_core/utils/public-links/services';
import { sortLevels } from '_core/utils/public-links/sort-levels';
import { customConfig } from '_features/custom/custom.config';

import { PageSeoTag } from '../../components/SEO';
import { ExploreAsideSection } from './components/explore-aside-section.component';
import { ExploreMainSection } from './components/explore-main-section.component';
import * as S from './styles';
import { exploreServicesFiltered, getDropdownToggleState } from './utils';

type ExplorePageProps = {
  scrollableCtn?: ForwardedRef<HTMLDivElement>;
};

export function ExplorePage(props: ExplorePageProps) {
  const { scrollableCtn } = props;

  const { data: links } = useGetLinks(PublicApiRepo.publicApiUrl);
  const { data: page } = useGetPage(PublicApiRepo.publicApiUrl);
  const attributes = page?.data.attributes;
  const { canonical } = usePublicAppContext();
  const { t } = useTranslation(customConfig.i18nNamespaces);
  const { executeScroll, addToRefs } = useScrollWithRefs(scrollableCtn);

  const [selectedId, setSelectedId] = useState('');
  const [isOpenAsideDropdown, setIsOpenAsideDropdown] = useState(getDropdownToggleState(links, 'aside'));
  const [isOpenServices, setIsOpenServices] = useState(getDropdownToggleState(links, 'main'));

  const openDropdown = (id: string): void => {
    setSelectedId(id);
    setIsOpenServices({
      ...isOpenServices,
      [id]: !isOpenServices[id],
    });
  };

  const openAsideDropdown = (id: string): void => {
    setSelectedId(id);
    setIsOpenAsideDropdown({
      ...isOpenAsideDropdown,
      [id]: !isOpenAsideDropdown[id],
    });
  };

  const exploreServicesByName = useMemo(
    () => createLevels(links, services(links, 'explore_services_by_name', exploreServicesFiltered), sortLevels),
    [links],
  );

  const exploreServicesByLocationMain = useMemo(
    () =>
      createLevels(links, services(links, 'explore_services_by_location_main', exploreServicesFiltered), sortLevels),
    [links],
  );

  const exploreServicesByLocationOthers = useMemo(
    () =>
      createLevels(links, services(links, 'explore_services_by_location_others', exploreServicesFiltered), sortLevels),
    [links],
  );

  if (!page) return null;

  return (
    <Fragment>
      {attributes && (
        <PageSeoTag
          nextSeoProps={{
            nofollow: !attributes.indexable,
            noindex: !attributes.indexable,
          }}
          canonical={canonical}
          title={attributes.metadata.title_tag}
          description={attributes.metadata.metadescription}
        />
      )}
      <Fragment>
        <S.Header className="pt-32 pb-16 bg-secondary-50">
          <div className="container-lg text-left">
            <S.Title className="text-break-word serif bold mb-2">{t('explore:title')}</S.Title>
            <p className="text-secondary-500 text-break-word">{t('explore:description')}</p>
          </div>
        </S.Header>
        <div className="container-lg p-0 layout-row" data-testid="explore-services">
          <section className="pt-16 pb-8 px-4 flex-1">
            <ExploreMainSection
              addToRefs={addToRefs}
              selectedId={selectedId}
              openDropdown={openDropdown}
              isOpenServices={isOpenServices}
              subtitle={attributes?.title.subtitle || ''}
              exploreServicesByName={exploreServicesByName}
              exploreServicesByLocationMain={exploreServicesByLocationMain}
              exploreServicesByLocationOthers={exploreServicesByLocationOthers}
            />
          </section>
          <div className="layout-column pt-16 pb-8 px-4 flex-33 hide show-gt-sm" data-testid="aside-card-section">
            <ExploreAsideSection
              selectedId={selectedId}
              openDropdown={openDropdown}
              executeScroll={executeScroll}
              openAsideDropdown={openAsideDropdown}
              title={attributes?.title.subtitle || ''}
              isOpenAsideDropdown={isOpenAsideDropdown}
              exploreServicesByName={exploreServicesByName}
              exploreServicesByLocationMain={exploreServicesByLocationMain}
              exploreServicesByLocationOthers={exploreServicesByLocationOthers}
            />
          </div>
        </div>
      </Fragment>
    </Fragment>
  );
}
